/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import Invoice from '../../models/invoiceModel';
import {
    AccessKey,
    ActivateHandle,
    CompleteInvoiceFilter,
    DashboardPeriod,
    IdleState,
    InvoiceFilter,
    InvoiceRequestStatus,
    InvoiceType,
    InvoiceUploadStats,
    OrderCardDetail,
    OrderStatus
} from '../../util/interfaces';
import {
    copyToClipboard,
    directTimeout,
    downloadFile,
    getSearchParams,
    navigate,
    nullify,
    ResponseData,
    showDialog,
    showSnackbar,
    stopPropagation,
    useEvent
} from '@dvrd/dvr-controls';
import {AppContext} from '../misc/appContext';
import {
    downloadInvoice,
    getInvoiceRequestStats,
    getInvoiceStats,
    getOrderCardDetails,
    uploadInvoice
} from '../../util/requestSender';
import Dashboard from './dashboard';
import {clearNavigationParam, setNavigationParam} from '../../util/navigateUtil';

import {onConfirmCheckout, onInvoicePreview, onSetCrumbs} from '../../util/events';
import {sendGaEvent, sendGaPageView} from '../../util/analytics';
import {createErrorMessage, warnTwoFactor} from '../../util/utils';
import AccountStatusChecker from '../account/pinger/statusPinger';
import {saveToLocal} from '../../util/storageUtil';
import ReverseChargeController from '../reverseCharge/reverseChargeController';
import FirstImportController from './firstImport/firstImportController';
import AssignInvoiceNumberController from './invoiceNumber/assignInvoiceNumberController';
import {onConnectBolOauth} from '../../util/bolOauthUtil';
import SendInvoiceController from '../invoice/send/sendInvoiceController';
import {useIsFirstRender} from '../../util/componentUtil';
import {SELECTED_ACCOUNT_KEY, SENDING_HISTORY_MAILS_KEY} from '../../util/constants';
import Account from '../../models/accountModel';
import {useLocation} from 'react-router';
import {getOpenInvoiceRequestCount} from '../../util/invoiceRequestUtil';
import useRight from '../misc/useRight';
import {useIdleObserver} from '../misc/idleObserver';
import {useTranslation} from 'react-i18next';

const DEBOUNCE = 1000;
const DEFAULT_LIMIT = 20;
// Per-request page size for a reload — mirrors the backend GetInvoices clamp
// (MAX_INVOICE_PAGE_LIMIT). A single request must never ask for more, or the
// server would clamp it and the reload would collapse the loaded list. Keep in
// sync with the backend value.
const RELOAD_PAGE_SIZE = 50;
// Ceiling on the loaded window a reload rebuilds — bounds the number of paged
// requests (window / RELOAD_PAGE_SIZE). The window only grows via repeated
// "Meer laden" (10 rows each), so this is a generous backstop.
const MAX_RELOAD_INVOICES = 500;
// Auto trigger a "load more" when row "PAD_ROWS" from the bottom is visible
const LOAD_MORE_PAD_ROWS = 5;

function getEmptyUploadStats(): InvoiceUploadStats {
    return {
        open: getOpenInvoiceRequestCount(),
        closed: {total: 0, thirty_days: 0},
        failed: 0
    };
}

export enum DashboardTab {
    INVOICE_REQUESTS,
    INVOICES
}

function determineTab(pathname: string): DashboardTab {
    if (pathname.endsWith('aanvragen')) return DashboardTab.INVOICE_REQUESTS;
    return DashboardTab.INVOICES;
}

export default function DashboardController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t} = useTranslation();
    const [canSeeStats] = useRight(AccessKey.INVOICE_STATS);
    const location = useLocation();
    const [invoices, setInvoices] = useState<Array<Invoice>>([]);
    const [hasMore, setHasMore] = useState(false);
    const [totalInvoices, setTotalInvoices] = useState(0);
    const [uploadStats, setUploadStats] = useState<InvoiceUploadStats>(getEmptyUploadStats());
    const [loadingUploadStats, setLoadingUploadStats] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filter, setFilter] = useState<InvoiceFilter>({});
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [isImportingHistory, setIsImportingHistory] = useState(false);
    const [sendingHistory, setSendingHistory] = useState(account?.getFlex(SENDING_HISTORY_MAILS_KEY) ?? false);
    const [tab, setTab] = useState<DashboardTab>(determineTab(location.pathname));
    const loadTimer = useRef<number | undefined>(undefined);
    const searchDebounce = useRef<number | null>(null);
    const reverseChargeRef = useRef<ActivateHandle>(null);
    const firstImportRef = useRef<FirstImportController>(null);
    const invNumRef = useRef<ActivateHandle>(null);
    const sendRef = useRef<ActivateHandle>(null);
    const invoiceAbortController = useRef<AbortController | null>(null);
    const statsAbortController = useRef<AbortController | null>(null);
    // Lazy per-card enrichment (see onCardVisible/flushCardDetails): the ids of
    // cards seen this load (dedup), the ids awaiting the next batched request,
    // and the coalescing timer for a scroll burst.
    const enrichRequestedRef = useRef<Set<string>>(new Set());
    const enrichPendingRef = useRef<Set<string>>(new Set());
    const enrichTimerRef = useRef<number | null>(null);
    // Kept in sync each render so the debounced flush targets the current
    // account without capturing a stale closure.
    const accountIdRef = useRef<string | null>(null);
    accountIdRef.current = account?.id ?? null;
    const hasAPIConnection = useMemo(() => !!account?.apiKeys.length, [account]);
    const isFirstRender = useIsFirstRender();
    useEvent('onResetDashboard', onReset);
    useEvent('updateInvoiceRequestCount', updateOpenRequestCount);
    useIdleObserver(onWakeStateChange);

    function onWakeStateChange(currentState: IdleState) {
        if (currentState === IdleState.IDLE) clearTimeouts();
        else loadInvoices(false);
    }

    function updateOpenRequestCount(count: number) {
        setUploadStats({...uploadStats, open: count});
    }

    function onCopyInvoiceID(invoice: Invoice) {
        return function () {
            copyToClipboard(invoice.id).then(() => {
                showSnackbar(`'${invoice.id}' ${t('dashboard.copied', 'gekopieerd')}`);
            });
        };
    }

    function onSearch() {
        // Enter-submit of the search field: always from the top (no cursor) —
        // after "Meer laden" a stale cursor would continue past the search hit.
        loadInvoices(true, {force: true})();
    }

    function onLocationChange() {
        const {pathname} = location;
        if (pathname.endsWith('aanvragen')) {
            setTab(DashboardTab.INVOICE_REQUESTS);
        } else if (pathname.endsWith('orders')) setTab(DashboardTab.INVOICES);
    }

    function onReset() {
        setFilter({});
        setLimit(DEFAULT_LIMIT);
        cancelRequests();
        // force: the abort above clears loadingInvoices asynchronously, so
        // without it the guard would still see the old true and skip.
        loadInvoices(true, {filter: {}, force: true})();
        loadStats();
        loadUploadStats();
    }

    function onClickDetails(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur details');
            changeAccountIfNeeded(invoice, () => {
                navigate(`/factuur-details?id=${invoice.id}`);
            });
        };
    }

    function onClickEdit(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur wijzigen');
            changeAccountIfNeeded(invoice, () => {
                if (invoice.invoiceType === InvoiceType.CREDIT)
                    navigate(`/credit-data?invoice-id=${invoice.referenceInvoiceId}`);
                else {
                    setNavigationParam(invoice.id);
                    navigate('/factuur-wijzigen');
                }
            });
        };
    }

    function onClickReverseCharge(invoice: Invoice) {
        return function () {
            changeAccountIfNeeded(invoice, () => {
                reverseChargeRef.current?.activate(invoice);
            });
        };
    }

    function onClickMail(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur verzenden');
            changeAccountIfNeeded(invoice, () => {
                sendRef.current?.activate(invoice);
            });
        };
    }

    function onUploadInvoice(invoice: Invoice) {
        return function () {
            setLoadingInvoices(true);
            uploadInvoice({
                data: {invoice_id: invoice.id, account_id: invoice.accountId},
                callback: (data: ResponseData) => {
                    setLoadingInvoices(false);
                    if (data.success) {
                        invoice.uploadStatus = InvoiceRequestStatus.UPLOADING;
                        onUpdateInvoice(invoice);
                        showSnackbar(t('dashboard.uploading.success', 'De factuur wordt geüpload naar Bol'));
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('dashboard.uploading.failed', 'Het uploaden van de factuur is niet gelukt.')
                            ),
                            t('dashboard.uploading.failedTitle', 'Uploaden mislukt')
                        );
                }
            });
        };
    }

    function onClickDownload(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur downloaden');
            changeAccountIfNeeded(invoice, () => {
                setLoadingInvoices(true);
                invoice.download(
                    (invoice: Invoice, blob: Blob | null, success: boolean, data: ResponseData, response: Response) => {
                        setLoadingInvoices(false);
                        if (success && blob !== null) {
                            void reloadLoadedWindow();
                            const header = response.headers.get('Content-Disposition');
                            let fileName = `${invoice.reference}.pdf`;
                            if (header)
                                fileName = header.substring(
                                    header.indexOf('filename=') + 'filename='.length,
                                    header.length
                                );
                            downloadFile(blob, fileName);
                        } else {
                            if (!data.handled)
                                showDialog(
                                    createErrorMessage(
                                        data.message ??
                                            t(
                                                'dashboard.download.invoiceFailed',
                                                'Het downloaden van de factuur is niet gelukt.'
                                            )
                                    ),
                                    t('dashboard.download.failedTitle', 'Downloaden mislukt')
                                );
                        }
                    }
                );
            });
        };
    }

    function onPreviewFile(invoice: Invoice) {
        return function () {
            sendGaEvent('Click', 'Dashboard', 'Factuur preview');
            changeAccountIfNeeded(invoice, () => {
                setLoadingInvoices(true);
                downloadInvoice({
                    invoice_id: invoice.id,
                    account_id: invoice.accountId,
                    noHistory: true,
                    callback: (data: ResponseData, response: Response) => {
                        setLoadingInvoices(false);
                        if (data.status === 'success')
                            // FILE RESPONSE
                            response.blob().then((blob: Blob) => {
                                onInvoicePreview(URL.createObjectURL(blob));
                            });
                        else if (!data.handled)
                            showDialog(
                                createErrorMessage(data.message ?? 'Het downloaden van de factuur is niet gelukt.'),
                                'Downloaden mislukt'
                            );
                    }
                });
            });
        };
    }

    // One keyset page of a reload, wrapped as a promise. Resolves null when this
    // reload was superseded (a newer request took over invoiceAbortController),
    // aborted, or the request failed — the caller then bails without touching the
    // list the newer request is (or will be) building. On success the controller
    // is left set so the loop's next page reassigns it (or it's cleared when the
    // loop finishes), keeping the auto-poll's "request in flight" guard true for
    // the whole reload.
    function fetchReloadPage(
        accountId: string,
        filters: InvoiceFilter,
        cursor: {date: string; id: string} | null
    ): Promise<{invoices: Invoice[]; data: ResponseData} | null> {
        return new Promise(resolve => {
            const controller = Invoice.getAll(
                accountId,
                filters,
                RELOAD_PAGE_SIZE,
                -1,
                (pageInvoices: Invoice[], success: boolean, data: ResponseData) => {
                    if (invoiceAbortController.current !== controller) return resolve(null);
                    if (!success) {
                        invoiceAbortController.current = null;
                        return resolve(null);
                    }
                    resolve({invoices: pageInvoices, data});
                },
                () => {
                    if (invoiceAbortController.current === controller) invoiceAbortController.current = null;
                    resolve(null);
                },
                cursor
            );
            invoiceAbortController.current = controller;
        });
    }

    // Refreshes everything currently on screen (the whole loaded window, fresh
    // from the top) and swaps it in atomically. The server caps a single request
    // at RELOAD_PAGE_SIZE, so a window grown past that by repeated "Meer laden" is
    // rebuilt by walking the keyset cursor in RELOAD_PAGE_SIZE chunks — a one-shot
    // oversized request would be clamped and collapse the appended list.
    async function reloadLoadedWindow(load: boolean = false) {
        if (!account) return;
        cancelRequests();
        if (load) setLoadingInvoices(true);
        const accountId = account.id;
        const filters = prepareFilters(filter);
        const target = Math.min(invoices.length || DEFAULT_LIMIT, MAX_RELOAD_INVOICES);
        const rebuilt: Invoice[] = [];
        const seen = new Set<string>();
        let cursor: {date: string; id: string} | null = null;
        let lastData: ResponseData | null = null;
        // The server still has rows past the rebuilt window (last page was full).
        let hasMoreBeyondWindow = false;
        while (rebuilt.length < target) {
            const page = await fetchReloadPage(accountId, filters, cursor);
            // null = superseded / aborted / failed: bail without clobbering the
            // list, and leave loading state to whichever request is now in charge.
            if (!page) {
                if (load) setLoadingInvoices(false);
                return;
            }
            lastData = page.data;
            for (const invoice of page.invoices) {
                if (seen.has(invoice.id)) continue;
                seen.add(invoice.id);
                rebuilt.push(invoice);
            }
            if (page.invoices.length < RELOAD_PAGE_SIZE) {
                hasMoreBeyondWindow = false; // a short page means the server ran out
                break;
            }
            hasMoreBeyondWindow = true;
            const last = page.invoices[page.invoices.length - 1];
            cursor = {date: last.date, id: last.id};
        }
        invoiceAbortController.current = null;
        if (load) setLoadingInvoices(false);
        if (!lastData) return;
        // Fresh instances: forget what the previous list enriched so the now-visible
        // cards request their image/returns/profit again.
        resetCardDetails();
        setInvoices(rebuilt.slice(0, target));
        // hasMore only when we actually reached the window edge on a full page, so a
        // later "Meer laden" past it still has rows to fetch.
        applyInvoiceListMeta(lastData, hasMoreBeyondWindow && rebuilt.length >= target);
    }

    function onReload() {
        void reloadLoadedWindow(true);
    }

    function onChangeSearch(value: string) {
        const sanitizedValue: string | null = nullify(value);
        // Stop if sanitized value is same as current value
        if (filter.order_id === sanitizedValue) return;
        clearDebounce();
        // Merge — the search box owns ONLY order_id. Replacing the whole
        // filter here silently dropped every other active filter (and made
        // the clear button a clear-all).
        const invoiceFilter: InvoiceFilter = {...filter, order_id: sanitizedValue};
        setFilter(invoiceFilter);
        searchDebounce.current = window.setTimeout(() => {
            sendGaEvent('Search', 'Dashboard', 'Zoeken');
            cancelRequests();
            loadInvoices(true, {filter: invoiceFilter, force: true})();
        }, DEBOUNCE);
    }

    function onClickApiFlow(evt: React.MouseEvent) {
        sendGaEvent('Click', 'Dashboard', 'API keys instellen');
        if (!account) return;
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth(evt.shiftKey);
    }

    function onClickCredit(invoice: Invoice) {
        return function () {
            changeAccountIfNeeded(invoice, () => {
                if (invoice.invoiceType === InvoiceType.CREDIT) {
                    setNavigationParam(invoice);
                    navigate('/factuur-details');
                } else if (invoice.hasCreditInvoice) {
                    clearNavigationParam();
                    navigate(`/factuur-details?id=${invoice.creditInvoiceId}`);
                } else navigate(`/credit-data?invoice-id=${invoice.id}`);
            });
        };
    }

    function onOpenFilters() {
        setFilterOpen(true);
    }

    function onCloseFilters() {
        setFilterOpen(false);
    }

    function onSubmitFilters(filters: CompleteInvoiceFilter) {
        onCloseFilters();
        setFilter(filters);
        // Abort any in-flight load and force past the loading guard — without
        // this a hung request would silently swallow the submit/clear reload.
        cancelRequests();
        loadInvoices(true, {filter: filters, force: true})();
    }

    function onChangeStatusFilter(orderStatus: OrderStatus | '') {
        // nullify('') → null, but its return type keeps the '' member.
        const status = nullify(orderStatus) as OrderStatus | null;
        const nextFilter: InvoiceFilter = {...filter, order_status: status};
        setFilter(nextFilter);
        // The quick filter is always clickable (it sits outside the list's
        // loader overlay), so an in-flight load must not swallow the reload:
        // abort it and force past the loading guard.
        cancelRequests();
        loadInvoices(true, {filter: nextFilter, force: true})();
    }

    function onFirstOldImport() {
        const firstRow = document.querySelector('div.invoices div.order-card');
        if (!firstRow || !firstImportRef.current) return;
        firstRow.classList.toggle('first-order', true);
        // Force DOM manipulation before activation
        directTimeout(() => {
            firstImportRef.current?.onActivate();
        });
    }

    function onClickAssignInvoiceNumber(invoice: Invoice) {
        return function (evt?: KeyboardEvent | React.MouseEvent) {
            if (evt) stopPropagation(evt);
            changeAccountIfNeeded(invoice, () => {
                invNumRef.current?.activate(invoice);
            });
        };
    }

    function onCancelEmails(invoice: Invoice) {
        return function () {
            showDialog(
                t(
                    'dashboard.cancelEmails.confirmBody',
                    'Weet je zeker dat je geen automatische mails voor deze bestelling meer wilt verzenden?'
                ),
                t('dashboard.cancelEmails.title', 'Mails annuleren'),
                [
                    t('common.no', 'Nee'),
                    {label: t('common.yes', 'Ja'), onClick: onConfirmCancelEmails(invoice), primary: false}
                ]
            );
        };
    }

    function onConfirmCancelEmails(invoice: Invoice) {
        return function () {
            setLoadingInvoices(true);
            changeAccountIfNeeded(invoice, () => {
                invoice.cancelEmails((invoice: Invoice, success: boolean, data: ResponseData) => {
                    setLoadingInvoices(false);
                    if (success) {
                        onUpdateInvoice(invoice);
                        showSnackbar(
                            t('dashboard.cancelEmails.success', 'Mails voor deze factuur worden niet meer verzonden')
                        );
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('dashboard.cancelEmails.failed', 'Het annuleren van de mails is niet gelukt')
                            ),
                            t('dashboard.cancelEmails.failedTitle', 'Annuleren mislukt')
                        );
                });
            });
        };
    }

    function onUpdateInvoice(invoice: Invoice) {
        const _invoices = invoices.slice();
        for (let i = 0; i < _invoices.length; i++) {
            if (_invoices[i].id === invoice.id) {
                _invoices.splice(i, 1, invoice);
                break;
            }
        }
        setInvoices(_invoices);
    }

    function onSendSuccess(invoice: Invoice) {
        onUpdateInvoice(invoice);
        loadStats();
    }

    function changeAccountIfNeeded(invoice: Invoice, callback: VoidFunction) {
        const {account, accounts} = context.accountContext;
        if (invoice.accountId === account?.id) return callback();
        const invoiceAccount = accounts.find((account: Account) => account.id === invoice.accountId);
        if (invoiceAccount) context.onChangeContext({account: invoiceAccount}, callback);
    }

    function loadData(accountChanged: boolean = false) {
        if (accountChanged) {
            setFilter({});
            // force: a request of the previous account may still be running —
            // the account switch must never be swallowed by the guard.
            loadInvoices(accountChanged, {filter: {}, force: true})();
            loadStats();
            loadUploadStats();
        }
    }

    function prepareFilters(filters?: InvoiceFilter): InvoiceFilter {
        if (!filters) return {};
        for (const key of Object.keys(filters)) filters[key] = nullify(filters[key]);
        return filters;
    }

    // Shared tail for a fresh / reloaded (non-append) invoice list: the "more to
    // load" flag plus the import/sending context the auto-poll re-arms on.
    function applyInvoiceListMeta(data: ResponseData, hasMore: boolean) {
        setHasMore(hasMore);
        updateContext(new Account(data.account));
        setIsImportingHistory(data.is_importing);
        setSendingHistory(data.sending_history_mails);
    }

    function attemptLoadMore(ix: number) {
        if (!hasMore) return; // No more to load so no need to check

        const currentTotal = invoices.length;
        if (currentTotal - LOAD_MORE_PAD_ROWS !== ix) return; // Not the "load more" row

        const last = invoices[currentTotal - 1];
        if (!last) return;
        setLoadingMore(true);
        loadInvoices(false, {cursor: {date: last.date, id: last.id}, append: true, force: true})();
    }

    function loadInvoices(
        load: boolean = true,
        params?: {
            filter?: InvoiceFilter;
            // Keyset cursor for "load more": continue strictly past this row.
            // Absent = load from the top (fresh list / reload).
            cursor?: {date: string; id: string} | null;
            period?: DashboardPeriod;
            limit?: number;
            // Appends the fetched batch to the list (load more) instead of
            // replacing it.
            append?: boolean;
            // Skips the loading guard — for callers that abort the in-flight
            // request (cancelRequests) and reload with different parameters.
            force?: boolean;
        }
    ) {
        return function () {
            clearDebounce();
            if (loadingInvoices && !params?.force) return;
            if (!account) return;
            const _filter = params?.filter ?? filter;
            const _cursor = params?.cursor ?? null;
            const _limit = params?.limit ?? limit;
            if (load) setLoadingInvoices(true);
            const accountId = account.id;
            const filters = prepareFilters(_filter);
            // Capture the controller instance: the abort of a superseded
            // request resolves asynchronously and must not clear the state of
            // the request that replaced it (nor may its late response land).
            const controller = Invoice.getAll(
                accountId,
                filters,
                _limit,
                -1, // page unused for the orders list — the cursor drives "load more"
                (invoices: Invoice[], success: boolean, data: ResponseData) => {
                    if (invoiceAbortController.current !== controller) return;
                    invoiceAbortController.current = null;
                    setLoadingInvoices(false);
                    setLoadingMore(false);
                    if (!success) return;
                    const {imported_from_api, previously_imported_old_order} = data;
                    if (params?.append) {
                        // Keyset returns rows strictly after the cursor, so there
                        // is no overlap; the id dedupe stays purely as a safety net.
                        // Appended cards enrich themselves as they scroll into view;
                        // the requested-set carries over so earlier cards don't refetch.
                        setInvoices(current => {
                            const seen = new Set(current.map((invoice: Invoice) => invoice.id));
                            return current.concat(invoices.filter((invoice: Invoice) => !seen.has(invoice.id)));
                        });
                    } else {
                        // Fresh list: forget what the previous list enriched so the
                        // now-visible cards (new Invoice instances) request again.
                        resetCardDetails();
                        setInvoices(invoices);
                    }
                    // A full batch suggests more pages behind it.
                    applyInvoiceListMeta(data, invoices.length === _limit);
                    if (imported_from_api) {
                        if (!previously_imported_old_order) window.setTimeout(onFirstOldImport);
                        else if (invoices.length === 1) {
                            onClickAssignInvoiceNumber(invoices[0])();
                        }
                    }
                },
                () => {
                    if (invoiceAbortController.current !== controller) return;
                    invoiceAbortController.current = null;
                    setLoadingInvoices(false);
                    setLoadingMore(false);
                },
                _cursor
            );
            invoiceAbortController.current = controller;
        };
    }

    function updateContext(nextAccount: Account) {
        if (account?.historyImported === nextAccount.historyImported) return;
        context.onChangeContext({account: nextAccount});
    }

    function loadStats() {
        if (!account || !canSeeStats) return;
        statsAbortController.current = getInvoiceStats({
            data: {
                account_id: account.id
            },
            callback: (data: ResponseData) => {
                statsAbortController.current = null;
                if (!data.success) return;
                const {order_count} = data;
                // The order total next to the list title now comes from the
                // top stats (not the invoice list load, which stays lean).
                setTotalInvoices(order_count ?? 0);
            },
            onAbort: () => {
                statsAbortController.current = null;
            }
        });
    }

    // Per-order enrichment (returns, product preview/image, profit) loads lazily,
    // one card at a time as it scrolls into view — so only the orders the user
    // actually sees cost a lookup, and the first-visible cards are ready fast
    // instead of waiting on the whole page. OrderCard reports visibility here.
    function onCardVisible(ix: number) {
        return function (invoiceId: string) {
            // Seen already this load (in flight or done) — nothing to do.
            if (enrichRequestedRef.current.has(invoiceId)) return;
            enrichRequestedRef.current.add(invoiceId);
            enrichPendingRef.current.add(invoiceId);
            scheduleCardDetailsFlush();
            attemptLoadMore(ix);
        };
    }

    // Coalesce a burst of cards revealed together (initial paint, a fast scroll)
    // into a single batched request rather than one per card.
    function scheduleCardDetailsFlush() {
        if (enrichTimerRef.current !== null) return;
        enrichTimerRef.current = window.setTimeout(() => {
            enrichTimerRef.current = null;
            flushCardDetails();
        }, 60);
    }

    // Fetch the queued cards' enrichment and merge it into the invoice models by
    // id, so a late response for invoices no longer in the list is simply ignored.
    function flushCardDetails() {
        const invoiceIds = [...enrichPendingRef.current];
        enrichPendingRef.current.clear();
        const accountId = accountIdRef.current;
        if (!invoiceIds.length || !accountId) return;
        getOrderCardDetails({
            account_id: accountId,
            invoice_ids: invoiceIds,
            callback: (data: ResponseData) => {
                if (!data.success) return;
                const details = (data.details ?? {}) as Record<string, OrderCardDetail>;
                setInvoices(current => {
                    let changed = false;
                    for (const invoice of current) {
                        const detail = details[invoice.id];
                        if (!detail) continue;
                        invoice.setField('return_status', detail.return_status);
                        if (!invoice.productImageUrl && detail.product_image_url)
                            // Only override if the original request did not get a product image
                            invoice.productImageUrl = detail.product_image_url;
                        invoice.setField('profit', detail.profit);
                        changed = true;
                    }
                    return changed ? [...current] : current;
                });
            }
        });
    }

    // A fresh (non-append) load replaces every card, so drop the dedup/queue
    // state and cancel any pending batch — the new visible cards request anew.
    function resetCardDetails() {
        enrichRequestedRef.current.clear();
        enrichPendingRef.current.clear();
        if (enrichTimerRef.current !== null) {
            window.clearTimeout(enrichTimerRef.current);
            enrichTimerRef.current = null;
        }
    }

    function loadUploadStats() {
        if (!account) return;
        setLoadingUploadStats(true);
        getInvoiceRequestStats({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoadingUploadStats(false);
                if (data.success) setUploadStats(Object.assign({}, uploadStats, data.stats));
            }
        });
    }

    function clearDebounce() {
        if (searchDebounce.current !== null) {
            window.clearTimeout(searchDebounce.current);
            searchDebounce.current = null;
        }
    }

    function processParams() {
        // The orders list intentionally does NOT restore filters, page or limit
        // from the URL: every page load starts clean (filters reset on refresh)
        // and a hand-crafted ?limit=100 can no longer pull the whole account in
        // one request. Only the Stripe checkout return is still honoured.
        const params = getSearchParams(location.search);
        if (params.has('subscription') && params.get('subscription') === '1') {
            window.history.pushState({}, document.title, window.location.pathname);
            onConfirmCheckout();
        }
        loadInvoices(true, {filter: {}, limit: DEFAULT_LIMIT})();
    }

    function cancelRequests() {
        invoiceAbortController.current?.abort();
        statsAbortController.current?.abort();
    }

    function clearTimeouts() {
        window.clearTimeout(loadTimer.current);
        loadTimer.current = undefined;
    }

    useEffect(() => {
        if (tab === DashboardTab.INVOICES)
            onSetCrumbs([{label: t('dashboard.crumbs.invoices', 'Facturen'), route: '/facturen/orders'}]);
        else if (tab === DashboardTab.INVOICE_REQUESTS)
            onSetCrumbs([
                {label: t('dashboard.crumbs.invoiceRequests', 'Factuuraanvragen'), route: '/facturen/aanvragen'}
            ]);
    }, [tab, t]);

    useEffect(() => {
        sendGaPageView('/facturen');
        if (!user) navigate('/inloggen');
        else warnTwoFactor(user);
        processParams();
        loadStats();
        loadUploadStats();
    }, []);

    useEffect(() => {
        setFilter({});
        if (!isFirstRender) window.setTimeout(() => loadData(true));
    }, [account?.historyImported, account?.id]);

    useEffect(() => {
        onLocationChange();
    }, [location.pathname]);

    useEffect(() => {
        let delay: number;
        if (isImportingHistory)
            delay = 1000; // Orders might appear anytime, fast reload
        else if (sendingHistory)
            delay = 5000; // Sending might finish anytime, semi fast reload
        else return; // Don't auto reload
        loadTimer.current = window.setTimeout(() => {
            // Auto-poll only: skip while an invoices request is in flight
            // (e.g. a pending "Meer laden" append) instead of cutting it off,
            // and leave the stats request alone. The effect re-arms once the
            // running request lands, so no tick is lost.
            if (invoiceAbortController.current) return;
            // Reload the whole loaded window from the top; paged internally so a
            // window grown past the per-request cap still refreshes fully.
            void reloadLoadedWindow(false);
        }, delay);

        return function () {
            clearTimeouts();
        };
    }, [isImportingHistory, sendingHistory, reloadLoadedWindow, invoices]);

    // Cancel a queued enrichment batch if the dashboard unmounts mid-debounce.
    useEffect(() => {
        return () => {
            if (enrichTimerRef.current !== null) window.clearTimeout(enrichTimerRef.current);
        };
    }, []);

    return (
        <>
            <Dashboard
                onClickEdit={onClickEdit}
                onClickMail={onClickMail}
                onClickDownload={onClickDownload}
                totalInvoices={totalInvoices}
                filter={filter}
                onChangeSearch={onChangeSearch}
                onSearch={onSearch}
                invoices={invoices}
                onCloseFilters={onCloseFilters}
                loadingInvoices={loadingInvoices}
                onPreviewFile={onPreviewFile}
                onClickApiFlow={onClickApiFlow}
                onChangeStatusFilter={onChangeStatusFilter}
                filtersOpen={filterOpen}
                onClickDetails={onClickDetails}
                onClickCredit={onClickCredit}
                onSubmitFilters={onSubmitFilters}
                onClickReverseCharge={onClickReverseCharge}
                onOpenFilters={onOpenFilters}
                onClickAssignInvoiceNumber={onClickAssignInvoiceNumber}
                onCardVisible={onCardVisible}
                onCancelEmails={onCancelEmails}
                onReload={onReload}
                isImportingHistory={isImportingHistory}
                sendingHistory={sendingHistory}
                tab={tab}
                reloadNumOpen={loadUploadStats}
                uploadStats={uploadStats}
                loadingUploadStats={loadingUploadStats}
                onUploadInvoice={onUploadInvoice}
                onCopyInvoiceID={onCopyInvoiceID}
                hasAPIConnection={hasAPIConnection}
                loadingMore={loadingMore}
            />
            <AccountStatusChecker />
            <ReverseChargeController onSuccess={onReload} ref={reverseChargeRef} />
            <FirstImportController ref={firstImportRef} />
            <AssignInvoiceNumberController onUpdate={onUpdateInvoice} ref={invNumRef} />
            <SendInvoiceController
                onSuccess={onSendSuccess}
                onAssignInvoiceNumber={onClickAssignInvoiceNumber}
                onDownloadInvoice={onClickDownload}
                ref={sendRef}
            />
        </>
    );
}
